import { useEffect, useRef, useState } from 'react';
import { ArrowsOut, X } from '@phosphor-icons/react';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * ExpandableTextarea — a compact inline preview that opens a clean popup editor
 * when clicked, so long descriptions/notes are comfortable to read and edit.
 *
 * The inline field is read-only (a preview); all editing happens in the modal,
 * which writes straight back through `onChange`.
 */

interface Props {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  /** Inline preview height in rows. */
  rows?: number;
  className?: string;
  maxLength?: number;
  disabled?: boolean;
  id?: string;
}

const baseField =
  'w-full rounded-md border border-border bg-card px-3 py-2 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-600/40';

export function ExpandableTextarea({
  value,
  onChange,
  label = 'Text',
  placeholder,
  rows = 4,
  className,
  maxLength,
  disabled,
  id,
}: Props) {
  const [open, setOpen] = useState(false);
  const modalRef = useRef<HTMLTextAreaElement>(null);

  // Close on Escape; lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    modalRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Page scroll lock: <ScrollLock /> inside the scrim below. It replaced a local
  // `overflow: hidden` here, which iOS Safari ignored for touch scrolling.

  return (
    <>
      <div className={`relative ${className ?? ''}`}>
        <textarea
          id={id}
          readOnly
          disabled={disabled}
          value={value}
          rows={rows}
          placeholder={placeholder}
          onClick={() => !disabled && setOpen(true)}
          onFocus={() => !disabled && setOpen(true)}
          className={`${baseField} max-h-[40vh] cursor-pointer resize-none overflow-auto pr-9`}
        />
        {!disabled && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            title={`Expand ${label.toLowerCase()}`}
            aria-label={`Expand ${label.toLowerCase()}`}
            className="absolute right-2 top-2 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowsOut className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <ScrollLock />
          <div className="my-auto flex h-[80vh] w-full max-w-3xl flex-col rounded-lg border border-border bg-card shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <h3 className="text-sm font-semibold text-foreground">{label}</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <textarea
              ref={modalRef}
              value={value}
              maxLength={maxLength}
              placeholder={placeholder}
              onChange={(e) => onChange(e.target.value)}
              className="flex-1 resize-none rounded-b-lg bg-card px-5 py-4 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
            <div className="flex items-center justify-between border-t border-border px-5 py-2.5">
              <span className="text-xs text-muted-foreground">
                {value.length.toLocaleString()} character{value.length === 1 ? '' : 's'}
                {maxLength ? ` / ${maxLength.toLocaleString()}` : ''}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md bg-brand-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
